import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { useReport } from '../contexts/ReportContext';
import { useCompanySetting } from '../contexts/CompanySettingContext';
import { formatDateToBackend, formatDateToFrontend, openFileWithLoading } from '../components/Helper';
import { reportNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import PageHeader from '../components/PageHeader';
import AddUserShift from '../components/AddUserShift';
import DateRangeSelection from '../components/DateRangeSelection';

function LabourReport() {
    const { currency } = useCompanySetting();
    const { userShiftClockOut } = useUser()
    const { labourReport, labourReportPDF, labourReportCSV } = useReport();
    const { restaurantId, hasPermission } = useAuth();

    const canEdit = useMemo(() => hasPermission("edit_labour_report", "access"), [hasPermission]);

    const currentDate = new Date();
    const yesterdayDate = new Date(currentDate);
    yesterdayDate.setDate(currentDate.getDate() - 1);

    const [dates, setDates] = useState([yesterdayDate, currentDate]);
    const [reportList, setReportList] = useState([]);
    const [overallTotalAmount, setOverallTotalAmount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [userName, setUserName] = useState(null);
    const [userId, setUserId] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [editShiftId, setEditShiftId] = useState(null);
    const [shiftVisibility, setShiftVisibility] = useState({});

    const startDate = (dates.length > 0 && dates[0]) ? formatDateToBackend(dates[0]) : null;
    const endDate = (dates.length > 0 && dates[1]) ? formatDateToBackend(dates[1]) : null;

    const startDateForFrontend = (dates.length > 0 && dates[0]) ? formatDateToFrontend(dates[0]) : null;
    const endDateForFrontend = (dates.length > 0 && dates[1]) ? formatDateToFrontend(dates[1]) : null;

    const fetchLabourReport = async () => {
        try {
            const scrollY = window.scrollY;

            setIsLoading(true);
            const reports = await labourReport(startDate, endDate);
            if (reports.data.userReports.length > 0) {
                setReportList(reports.data.userReports);
                setOverallTotalAmount(reports.data.overallTotalAmount)
            } else {
                setReportList([]);
            }
            setIsLoading(false);

            setTimeout(() => {
                window.scrollTo(0, scrollY);
            }, 0);
        } catch (error) {
            console.error('Error fetching labour reports', error);
        }
    };

    const forceClockOut = async (id) => {
        try {
            if (id) {
                await userShiftClockOut(id)
                fetchLabourReport()
            }
        } catch (error) {
            console.error('Error in force clock out', error);
        }
    }

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchLabourReport();
        }
    }, [restaurantId, startDate, endDate]);

    const handleClose = () => {
        setUserName(null);
        setVisible(false);
        setEditMode(false);
        setEditShiftId(null);
    };

    const toggleShiftVisibility = (labourId) => {
        setShiftVisibility((prevState) => ({
            ...prevState,
            [labourId]: !prevState[labourId],
        }));
    };

    const generatePDF = async (pdfType) => {
        openFileWithLoading({
            apiCall: labourReportPDF,
            apiParams: [startDate, endDate, pdfType],
            fileType: 'pdf',
            loadingMessage: `Generating ${pdfType === 'summary' ? 'Summary' : 'Detailed'} PDF...`,
            errorMessage: `Failed to generate ${pdfType === 'summary' ? 'Summary' : 'Detailed'} PDF`,
        });
    }

    const generateCSV = async (csvType) => {
        openFileWithLoading({
            apiCall: labourReportCSV,
            apiParams: [startDate, endDate, csvType],
            fileType: 'csv',
            loadingMessage: `Generating ${csvType === 'summary' ? 'Summary' : 'Detailed'} CSV...`,
            errorMessage: `Failed to generate ${csvType === 'summary' ? 'Summary' : 'Detailed'} CSV`,
        });
    }

    return (
        <Layout2 items={reportNav.items} navTitle={reportNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title={'Labour Reports'} />
                <div className='d-flex flex-row px-4 py-2 user-list-nav justify-content-between'>
                    <p className='pt-2'>
                        Download labour report: <span className='view_button' onClick={() => generatePDF('summary')}>Summary PDF</span> / <span className='view_button me-3' onClick={() => generateCSV('summary')}>Summary CSV</span> <span className='view_button' onClick={() => generatePDF('detailed')}>Detailed PDF</span> / <span className='view_button' onClick={() => generateCSV('detailed')}>Detailed CSV</span>
                    </p>
                    <DateRangeSelection
                        dates={dates}
                        setDates={setDates}
                    />
                </div>
                <div className='px-4 py-2'>
                    <div className='border-bottom mt-2 mb-3 d-flex justify-content-between'>
                        <p className='labour-clock-time'>User Clock Times From {startDateForFrontend} to {endDateForFrontend}</p>
                        <p className='labour-clock-time'>Total Expense: {currency}{overallTotalAmount}</p>
                    </div>
                    {
                        <AddUserShift
                            userId={userId}
                            visible={visible}
                            userName={userName}
                            editMode={editMode}
                            editShiftId={editShiftId}
                            handleClose={handleClose}
                            fetchLabourReport={fetchLabourReport}
                        />
                    }
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <div>
                            {reportList && reportList.length > 0 && (
                                reportList.map((labour) => (
                                    <table className='w-100 labour-table mb-4' key={labour.id}>
                                        <thead>
                                            <tr className='first-table-header'>
                                                <th className='left-side-part'></th>
                                                <th>Username</th>
                                                <th>Rate Per Hour</th>
                                                <th>Total Hours</th>
                                                <th>Total Amount</th>
                                                <th>First Clock In</th>
                                                <th>Last Clock Out</th>
                                                <th className='labour-report-action'>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className='left-side-part'></td>
                                                <td>{labour.user_name}</td>
                                                <td>{currency}{labour.rate_per_hour}</td>
                                                <td>{labour.totalHours}</td>
                                                <td>{currency}{labour.totalAmount}</td>
                                                <td>{labour.firstClockIn ? labour.firstClockIn : '-'}</td>
                                                <td>{labour.lastClockOut ? labour.lastClockOut : '-'}</td>
                                                <td className='labour-report-action'>
                                                    <p className='view_button' onClick={() => toggleShiftVisibility(labour.id)}>
                                                        {shiftVisibility[labour.id] ? 'Hide Detail' : 'View Detail'}
                                                    </p>
                                                </td>
                                            </tr>
                                        </tbody>
                                        {shiftVisibility[labour.id] && (
                                            <>
                                                <thead>
                                                    <tr className='second-table-header'>
                                                        <th className='left-side-part'></th>
                                                        <th colSpan="2" className='p-2'>Id</th>
                                                        {/* <th>Rate Per Hour</th> */}
                                                        <th>Shift Hours</th>
                                                        <th>Shift Amount</th>
                                                        <th>Clock In</th>
                                                        <th>Clock Out</th>
                                                        <th className='labour-report-action'>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {labour.shifts && labour.shifts.length > 0 ? (
                                                        labour.shifts.map((shift) => (
                                                            <tr className='second-table-body' key={shift.id}>
                                                                <td className='left-side-part'></td>
                                                                <td colSpan="2" className='p-2'>{shift.id}</td>
                                                                {/* <td>{currency}{labour.rate_per_hour}</td> */}
                                                                <td>{shift.clock_out ? shift.totalHours : '-'}</td>
                                                                <td>{shift.totalAmount ? `${currency}${shift.totalAmount}` : '-'}</td>
                                                                <td>{shift.clock_in}</td>
                                                                <td>{shift.clock_out ? shift.clock_out : '-'}</td>
                                                                <td className='labour-report-action'>
                                                                    {canEdit && (
                                                                        <p>
                                                                            <span
                                                                                className='view_button me-1'
                                                                                onClick={() => {
                                                                                    setUserName(labour.user_name);
                                                                                    setEditShiftId(shift.id);
                                                                                    setUserId(null);
                                                                                    setEditMode(true);
                                                                                    setVisible(false);
                                                                                }}>
                                                                                Edit
                                                                            </span>
                                                                            {
                                                                                !shift.clock_out && (
                                                                                    <>
                                                                                        / <span
                                                                                            className='view_button'
                                                                                            onClick={() => {
                                                                                                forceClockOut(shift.id)
                                                                                            }}>
                                                                                            Force Clock Out
                                                                                        </span>
                                                                                    </>
                                                                                )
                                                                            }
                                                                        </p>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr className='second-table-body'>
                                                            <td className='left-side-part'></td>
                                                            <td colSpan="6" className='text-center'>No Shifts Available</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </>
                                        )}
                                        <tfoot>
                                            <tr className='second-table-body'>
                                                <td className='left-side-part-last-row'></td>
                                                <td className='d-flex'>
                                                    {canEdit && (
                                                        <p
                                                            className='view_button'
                                                            onClick={() => {
                                                                setUserName(labour.user_name);
                                                                setUserId(labour.id);
                                                                setEditShiftId(null);
                                                                setEditMode(false);
                                                                setVisible(true);
                                                            }}>
                                                            Add Shift
                                                        </p>
                                                    )}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Layout2>
    );
}

export default LabourReport;
